// src/components/books/modals/ConditionModal.jsx
import React, { useState } from 'react';
import { X, Plus, Trash2, HelpCircle } from 'lucide-react';

export const ConditionModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentValue = '', 
  availableVariables = [] 
}) => {
  const [condition, setCondition] = useState(currentValue);
  const [selectedVariable, setSelectedVariable] = useState('');
  const [operator, setOperator] = useState('equals');
  const [value, setValue] = useState('');
  const [showHelper, setShowHelper] = useState(false);

  const operators = [
    { value: 'equals', label: 'Equals (==)', example: 'userName == "John"' },
    { value: 'not_equals', label: 'Not Equals (!=)', example: 'age != 18' },
    { value: 'greater', label: 'Greater Than (>)', example: 'score > 80' },
    { value: 'less', label: 'Less Than (<)', example: 'attempts < 3' },
    { value: 'contains', label: 'Contains', example: 'email contains "@gmail"' },
    { value: 'exists', label: 'Variable Exists', example: 'userName exists' },
    { value: 'empty', label: 'Is Empty', example: 'notes is empty' }
  ];

  const conditionExamples = [
    'userName == "Admin"',
    'score > 80',
    'attempts < 3',
    'email contains "@gmail.com"',
    'userType == "premium"',
    'age >= 18 && score > 50',
    'level == 1 || isVIP == true'
  ];

  const buildCondition = () => {
    if (!selectedVariable) return;
    
    let newCondition = '';
    switch (operator) {
      case 'equals':
        newCondition = `${selectedVariable} == "${value}"`;
        break;
      case 'not_equals':
        newCondition = `${selectedVariable} != "${value}"`;
        break;
      case 'greater':
        newCondition = `${selectedVariable} > ${value}`;
        break;
      case 'less':
        newCondition = `${selectedVariable} < ${value}`;
        break;
      case 'contains':
        newCondition = `${selectedVariable} contains "${value}"`;
        break;
      case 'exists':
        newCondition = `${selectedVariable} exists`;
        break;
      case 'empty':
        newCondition = `${selectedVariable} is empty`;
        break;
    }
    
    if (condition && newCondition) {
      setCondition(condition + ' && ' + newCondition);
    } else if (newCondition) {
      setCondition(newCondition);
    }
    
    // Reset form
    setSelectedVariable('');
    setValue('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-full max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Condition Builder</h2>
            <button
              onClick={() => setShowHelper(!showHelper)}
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

         <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Helper Guide */}
          {showHelper && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">How Conditions Work</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Conditions determine when a message should be shown</li>
                <li>• Use variables collected from previous user responses</li>
                <li>• Combine multiple conditions with && (AND) or || (OR)</li>
                <li>• If condition is empty, message always shows</li>
              </ul>
            </div>
          )}

          {/* Current Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Condition
            </label>
            <textarea
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
              rows="3"
              placeholder="Enter condition or use the builder below..."
            />
          </div>

          {/* Condition Builder */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-4">Visual Condition Builder</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Variable</label>
                <select
                  value={selectedVariable}
                  onChange={(e) => setSelectedVariable(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select variable...</option>
                  {availableVariables.map(variable => (
                    <option key={variable} value={variable}>{variable}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  {operators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Enter value..."
                  disabled={operator === 'exists' || operator === 'empty'}
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={buildCondition}
                  disabled={!selectedVariable}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4 mx-auto" />
                </button>
              </div>
            </div>

            {/* Operator Help */}
            <div className="text-xs text-gray-600">
              <strong>Current operator:</strong> {operators.find(op => op.value === operator)?.example}
            </div>
          </div>

          {/* Examples */}
          <div>
            <h3 className="font-medium mb-2">Common Examples</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {conditionExamples.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setCondition(example)}
                  className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded border font-mono"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(condition)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Condition
          </button>
        </div>
      </div>
    </div>
  );
};